// A finding that met a standard (or the several of one session, together),
// told in a few plain sentences for a reader
// who has never seen the session: what the person asked, what the agent did,
// why that is a problem, and how it ended. One completion on the chat's model
// (openPreviewModel), from what the Triage row already holds: the finding, the
// check's verdict, the standard that met it and the story drawn around it
// (findingStory). Secret-looking values are masked before the request, so a
// summary never repeats one. Answers are kept for the page's life, per finding
// and verdict.

// One prompt for every model. Tuned against the standard-met findings on
// staging across gpt-6-luna, claude-haiku-5.5 and glm-5.3-flash; the order is
// written as prose and the length repeated at the end of the input because a
// numbered order or a length stated once got numbered or overlong answers.
export const SUMMARY_SYSTEM = `You explain one flagged moment from an AI coding agent's session to someone reviewing it. They are not an engineer and have never seen this session.

Write one paragraph of plain prose: what the person asked the agent to do, then what the agent did that was flagged, then why that is a problem as a concrete consequence (what could go wrong, or what the person had said not to do), then how it ended if the record shows it. If the record does not show how it ended, stop after why it is a problem.

Facts:
- State only what the record shows. Never say what the record does not show, and never guess at intent.
- Where the independent check corrected the finding, tell what actually happened as plain fact (say "it gave the account permission to change files", not "the finding overstated it"). Do not mention the check, the finding, the standard, the rule or the review.

Words:
- Call them "the person". Never "you", "the user" or "the reviewer".
- Use everyday words. Say what a command did, not its syntax. Do not define terms in parentheses; pick a plainer word instead.
- Quote at most one short phrase, and only the person's own words.
- No preamble, no hedging ("it appears", "notably"), no advice, no closing summary.
- Masked values like [api key] or [email] stand for real values; refer to them naturally ("a key", "an outside email address").

Length: 2 or 3 sentences, at most 80 words. Never number, list or label the parts. Plain text, no formatting.`;
export const SUMMARY_TAIL = "Answer in 2 or 3 sentences, at most 80 words, as one plain paragraph with no lists.";

// Room for a reasoning model's thinking before its few sentences.
export const SUMMARY_MAX_TOKENS = 4000;
const MOMENT_CHARS = 1200;
const COMMAND_CHARS = 500;
const OUTPUT_CHARS = 900;
const QUOTED_CHARS = 600;
const STANDARD_CHARS = 1500;
const CACHE_MAX = 50;

const str = (v) => (v == null ? "" : String(v));
const clip = (s, n) => (s.length > n ? `${s.slice(0, n)}...` : s);

const MASKS = [
    [/-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z ]*PRIVATE KEY-----/g, "[private key]"],
    [/\b(AKIA|ASIA)[A-Z0-9]{16}\b/g, "[aws key id]"],
    [/\b(ghp|gho|ghs|ghu|github_pat)_[A-Za-z0-9_]{20,}\b/g, "[github token]"],
    [/\bsk-[A-Za-z0-9_-]{20,}\b/g, "[api key]"],
    [/\bxox[abprs]-[A-Za-z0-9-]{10,}\b/g, "[slack token]"],
    [/\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, "[jwt]"],
    [/(bearer\s+)[A-Za-z0-9._~+/=-]{16,}/gi, "$1[token]"],
    [/((?:password|passwd|secret|token|api[_-]?key|access[_-]?key)["']?\s*[:=]\s*["']?)[^\s"',;]{6,}/gi, "$1[secret]"],
    [/\b(?=[A-Za-z0-9+_-]*\d)(?=[A-Za-z0-9+_-]*[A-Za-z])[A-Za-z0-9+_-]{32,}={0,2}/g, "[long secret-like value]"],
    [/\b[a-f0-9]{40,}\b/gi, "[long hex value]"],
    [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[email]"],
    [/([\x5c/](?:Users|home)[\x5c/])[^\x5c/\s"']+/gi, "$1[user]"],
    [/(-Users-)[A-Za-z0-9._]+/g, "$1[user]"],
];
export function maskSecrets(text) {
    return MASKS.reduce((t, [re, to]) => t.replace(re, to), str(text));
}

const WHO = {
    person: "The person", notice: "System notice", ran: "The agent ran", result: "Result of the agent's command",
    failed: "The agent's command failed", said: "The agent said", thought: "The agent thought",
};
const KIND = { ask: "the person's request", failed: "a failure on the way", finding: "flagged", told: "the agent's answer to the person" };
const VERDICT = { confirmed: "confirmed true", partial: "partly true" };

const timeOf = (ms) => (Number.isFinite(ms) ? `${new Date(ms).toISOString().replace("T", " ").slice(0, 16)} UTC` : "");

// A call's moment as its command and what came back, split where the story
// joined them.
function callParts(m) {
    if (!["ran", "result", "failed"].includes(m.who)) return null;
    const t = str(m.quote);
    const cuts = [" -> ", " Returned: ", " Failed: ", "\n-> ", "\nReturned: ", "\nFailed: "]
        .map((s) => [t.indexOf(s), s]).filter(([i]) => i > 0).sort((a, b) => a[0] - b[0]);
    if (!cuts.length) return { command: t, output: null, failed: m.who === "failed" };
    const [i, sep] = cuts[0];
    return { command: t.slice(0, i), output: t.slice(i + sep.length), failed: sep.includes("Failed") || m.who === "failed" };
}

// One finding's lines in the request. `finding`: { title }. `verdict`: the
// check's (verdict, why, correctedClaim, metBy, standardWhy). `standard`:
// { name, text } of the standard that met it, text optional.
function findingLines({ finding, verdict, standard = null }, head = "Finding") {
    const lines = [`${head}: ${maskSecrets(finding?.title)}`];
    const standardName = standard?.name || verdict?.metBy?.[0] || "";
    if (standardName) lines.push(`Standard that flagged it: ${standardName}${standard?.text ? ` - "${clip(maskSecrets(standard.text), STANDARD_CHARS)}"` : ""}`);
    if (verdict?.standardWhy) lines.push(`Why the standard flagged it: ${maskSecrets(verdict.standardWhy)}`);
    if (verdict?.verdict) {
        lines.push(`Independent check of the finding: ${VERDICT[verdict.verdict] || verdict.verdict}${verdict.why ? ` - ${maskSecrets(verdict.why)}` : ""}`);
        if (verdict.correctedClaim) lines.push(`What the session actually shows: ${maskSecrets(verdict.correctedClaim)}`);
    }
    return lines;
}

// The request's input for one finding ({ finding, verdict, standard }, as
// findingLines takes them), or for several of one session told together
// (`findings`, each with `at`, its index in the story). `story`:
// findingStory's, every flag's moments in order. `others`: the titles of the
// session's other findings.
export function summaryInput({ finding, verdict, standard = null, findings = null, story, others = [] }) {
    const told = findings?.length ? findings : [{ finding, verdict, standard }];
    const several = told.length > 1;
    const lines = several
        ? [
            `${told.length} findings were flagged in this session. Tell them together, in the order they happened, as one paragraph.`,
            ...told.flatMap((f, j) => ["", ...findingLines(f, `Finding ${j + 1}`)]),
            "",
        ]
        : findingLines(told[0]);
    if (others.length) lines.push(`Other findings in the same session (not ${several ? "these" : "this one"}): ${others.map(maskSecrets).join("; ")}`);
    // Which of the told findings a flagged moment belongs to, where several are.
    // A message several cite is told once, as all of theirs.
    const numberOf = new Map(several ? told.map((f, j) => [f.at, j + 1]) : []);
    const numbersAt = new Map();
    for (const flag of story?.flags || []) {
        if (!numberOf.has(flag.finding)) continue;
        for (const m of flag.moments || []) if (m.turn != null) numbersAt.set(m.turn, [...(numbersAt.get(m.turn) || []), numberOf.get(flag.finding)]);
    }
    const toldTurns = new Set();
    lines.push("", "What happened, in order:");
    let lastCommand = null;
    for (const flag of story?.flags || []) {
        for (const m of flag.moments || []) {
            if (m.turn != null && toldTurns.has(m.turn)) continue;
            if (m.turn != null) toldTurns.add(m.turn);
            const numbers = m.turn != null ? numbersAt.get(m.turn) || [] : numberOf.has(flag.finding) ? [numberOf.get(flag.finding)] : [];
            const kind = `${KIND[flag.kind] || flag.kind}${numbers.length ? ` as finding${numbers.length > 1 ? "s" : ""} ${numbers.join(" and ")}` : ""}`;
            const head = `${WHO[m.who] || m.who} (${kind}${timeOf(m.at) ? `, ${timeOf(m.at)}` : ""})`;
            const call = callParts(m);
            // A result right after its own call says nothing the call's line did not.
            if (call && (m.who === "result" || m.who === "failed") && call.command === lastCommand) continue;
            lastCommand = call?.command ?? null;
            let body = call
                ? `${clip(maskSecrets(call.command), COMMAND_CHARS)}${call.output != null ? `\n  ${call.failed ? "It failed" : "It returned"}: ${clip(maskSecrets(call.output), OUTPUT_CHARS)}` : ""}`
                : clip(maskSecrets(m.quote), MOMENT_CHARS);
            const quoted = m.highlight ? maskSecrets(m.highlight) : "";
            if (quoted && !body.includes(clip(quoted, 300))) body += `\n  The check quoted: ${clip(quoted, QUOTED_CHARS)}`;
            lines.push(`- ${head}: ${body}`);
        }
    }
    return `${lines.join("\n")}\n\n${SUMMARY_TAIL}`;
}

// Answers kept for the page's life, and asks in flight shared, so reopening a
// row or two rows of one session ask once.
const kept = new Map();
export function summaryKey(findingId, verdict) {
    return `${str(findingId).toLowerCase()}|${str(verdict?.executionId || verdict?.verifiedAt)}`;
}
export function keptSummary(key) {
    return kept.get(key)?.text ?? null;
}
// -> Promise<{ text }>. `complete({ system, user, signal, maxTokens })`
// resolves to { text }. A failed ask is forgotten, so the next open asks again.
export function summarize({ key, input, complete }) {
    const have = kept.get(key);
    if (have) return have.promise;
    const entry = { text: null, promise: null };
    entry.promise = Promise.resolve()
        .then(() => complete({ system: SUMMARY_SYSTEM, user: input, maxTokens: SUMMARY_MAX_TOKENS }))
        .then((reply) => {
            const text = str(reply?.text).trim();
            if (!text) throw new Error("The model gave an empty answer.");
            entry.text = text;
            return { text };
        })
        .catch((e) => { if (kept.get(key) === entry) kept.delete(key); throw e; });
    kept.set(key, entry);
    while (kept.size > CACHE_MAX) kept.delete(kept.keys().next().value);
    return entry.promise;
}
export function _clearSummariesForTests() {
    kept.clear();
}
